import {
  Box,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { routes } from "src/shared/routes";
import { useGlobalStore } from "../../hooks/useGlobalStore";
import { createState } from "src/utils/create_zustand_store";

import { useSSTheme } from "../../hooks/useSSTheme";
import { useTeamConfig } from "../TeamConfigProvider";
import { usePurchaseFlow } from "src/components/feature/PurchaseFlow/usePurchaseFlow";
import { formatSeatLocation } from "src/shared/utils/seatLocation";

import { SSText } from "src/components/base/components/SSText";
import { DynamicModal } from "src/components/base/components/DynamicModal";

import type { TCart } from "src/shared/types/globalStotre.types";

type TSeatLocationsModal = {
  isOpen: boolean;
  cartRes: TCart | null;
  checkoutRes: any | null;
};

export const useSeatLocationsModal = createState<TSeatLocationsModal>({
  isOpen: false,
  cartRes: null,
  checkoutRes: null,
});

export function SeatLocationsModal() {
  const { colors } = useSSTheme();
  const { cart } = useGlobalStore();
  const { breakpoints } = useTheme();
  const modalState = useSeatLocationsModal();
  const [isLoading, setIsLoading] = useState(false);
  const { handlePurchaseSuccess } = usePurchaseFlow();
  const isMobile = useMediaQuery(breakpoints.down(900));
  const { IMAGES, TEAM_GLOBAL_SETTINGS } = useTeamConfig();

  async function onClose() {
    setIsLoading(true);
    await handlePurchaseSuccess(
      modalState.cartRes,
      modalState.checkoutRes,
      routes.purchase_history
    );
    modalState.reset();
    setIsLoading(false);
  }

  if (!cart?.products?.length) return null;

  const formattedProducts = cart?.products
    ?.filter((itm) => itm?.showSeatLocation)
    .map((itm) => {
      return {
        ...itm,
        location: formatSeatLocation(itm?.location || ""),
      };
    });

  return (
    <DynamicModal
      maxWidth="md"
      isOpen={modalState.isOpen}
      title="Purchase Confirmation"
      description={
        <Box gap={2} display="flex" flexDirection="column">
          <Typography>
            Enter the venue using your original tickets and proceed to your
            upgraded location(s) as shown.
          </Typography>
          <Stack py={1} gap={2} direction={{ xs: "column", md: "row" }}>
            <Stack width={"100%"} maxWidth={isMobile ? "100%" : 300} gap={2}>
              {formattedProducts.map((itm, idx) => (
                <Stack key={itm.productId} gap={2}>
                  <Stack spacing={1}>
                    <Stack spacing={0.2}>
                      <SSText
                        variant="body"
                        fontWeight={700}
                        lineHeight={1.1}
                        fontSize={"0.95rem"}
                        sx={{
                          background: `linear-gradient(90deg, ${TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY} 30%, #fff 50%, ${TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY} 70%)`,
                          backgroundSize: "200% 100%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "sheen 3s linear infinite",
                          "@keyframes sheen": {
                            "0%": { backgroundPosition: "-200% 0" },
                            "100%": { backgroundPosition: "200% 0" },
                          },
                        }}
                      >
                        {itm.productTitle}
                      </SSText>

                      {itm?.productDescription ? (
                        <SSText
                          variant="body"
                          lineHeight={1.1}
                          fontSize={"0.95rem"}
                          sx={{
                            opacity: 0.7,
                            marginTop: "4px !important",
                            background: `linear-gradient(90deg, ${colors.gray[400]} 30%, #fff 50%, ${colors.gray[400]} 70%)`,
                            backgroundSize: "200% 100%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            animation: "sheen 3s linear infinite",
                            "@keyframes sheen": {
                              "0%": { backgroundPosition: "-200% 0" },
                              "100%": { backgroundPosition: "200% 0" },
                            },
                          }}
                        >
                          {itm.productDescription}
                        </SSText>
                      ) : null}
                    </Stack>

                    <SSText
                      variant="body"
                      lineHeight={1.1}
                      fontWeight={700}
                      fontSize={"0.95rem"}
                      sx={{ opacity: 0.7 }}
                    >
                      {itm?.location}
                    </SSText>
                  </Stack>

                  {idx !== (formattedProducts?.length as number) - 1 ? (
                    <Divider flexItem />
                  ) : null}
                </Stack>
              ))}
            </Stack>

            {IMAGES?.SEAT_MAP_URL && (
              <Stack gap={2} direction={{ xs: "column", md: "row" }}>
                <Divider
                  flexItem
                  orientation={isMobile ? "horizontal" : "vertical"}
                />

                <Box width={"100%"}>
                  <SSText variant="h6" mb={1}>
                    Stage
                  </SSText>
                  <Box
                    sx={{
                      borderRadius: 1,
                      objectFit: "contain",
                    }}
                    alt="Seat Map"
                    width={"100%"}
                    component={"img"}
                    src={IMAGES?.SEAT_MAP_URL}
                    maxWidth={isMobile ? "100%" : 550}
                    maxHeight={isMobile ? "450px" : "unset"}
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      }
      onClose={async () => await onClose()}
      slot={{
        paper: {
          style: {
            margin: "0 8px",
          },
        },
        primaryButton: {
          isLoading,
          label: "Close",
          onClick: async () => await onClose(),
        },
        secondaryButton: {
          isHidden: true,
        },
      }}
    />
  );
}
